<template>
    <div class="vehicle-prices">
        <div class="vehicle-prices__subheading">{{ $t('pages.vehicle.seasonal_rates') }}</div>
        <p>{{ $t('pages.vehicle.seasonal_rates_subheading') }}</p>
        <Accordion
            class="vehicle-prices__seasons-accordion"
            slot-key="key"
            mode="card"
            :items="accordionItems"
            :show-default-header-title="false"
            @item-opened="index => (accordionItems[index].open = true)"
            @item-closed="index => (accordionItems[index].open = false)"
        >
            <template #header-high-season="{ item }">
                <div class="vehicle-prices__accordion-item-header">
                    <div>
                        <strong>{{ item.title }}</strong>
                        <span>{{ $t('pages.edit_vehicle_pricing.high_season_months') }}</span>
                    </div>
                    <i18n v-if="!accordionItems[0].open" path="pricing.from_x_per_night" tag="div">
                        <template #price>
                            <span class="vehicle-prices__per-night-amount">{{ highNightlyPrice }}</span>
                        </template>
                    </i18n>
                </div>
            </template>

            <template #header-low-season="{ item }">
                <div class="vehicle-prices__accordion-item-header">
                    <div>
                        <strong>{{ item.title }}</strong>
                        <span>{{ $t('pages.edit_vehicle_pricing.low_season_months') }}</span>
                    </div>
                    <i18n v-if="!accordionItems[1].open" path="pricing.from_x_per_night" tag="div">
                        <template #price>
                            <span class="vehicle-prices__per-night-amount">{{ lowNightlyPrice }}</span>
                        </template>
                    </i18n>
                </div>
            </template>

            <template v-for="seasonData in processedSeasonData" #[`item-${seasonData.key}`]>
                <div :key="seasonData.key + '-top'" class="vehicle-prices__seasonal-info-top">
                    <div>
                        <span>{{ $t('pages.vehicle.price_per_night') }} </span>
                        <span> {{ seasonData.nightlyPriceDisplay }} </span>
                    </div>
                    <div>
                        <span>{{ $t('pages.vehicle.minimum_rental_period') }} </span>
                        <span> {{ $tc('common.nights_count', seasonData.minBookingLengthVal) }} </span>
                    </div>
                </div>
                <div
                    v-if="seasonData.hasDiscount || seasonData.hasIncrease"
                    :key="seasonData.key + '-bottom'"
                    class="vehicle-prices__seasonal-info-bottom"
                >
                    <div v-if="seasonData.hasDiscount">
                        <strong>{{ $t('pages.vehicle.long_term_discount_title') }} </strong>
                        <i18n path="pages.vehicle.long_term_discount_description" tag="div">
                            <template #nightThreshold>{{ LONG_BOOKING_DISCOUNT_THRESHOLD }}</template>
                            <template #percentage>
                                <strong>{{ seasonData.discountPercentageDisplay }}</strong>
                            </template>
                        </i18n>
                    </div>
                    <div v-if="seasonData.hasIncrease">
                        <strong>{{ $t('pages.vehicle.short_booking_surcharge_title') }} </strong>
                        <i18n path="pages.vehicle.short_booking_surcharge" tag="div">
                            <template #PK_nights_count>
                                <strong>{{ $tc('common.nights_count', SHORT_BOOKING_SURCHARGE_THRESHOLD) }}</strong>
                            </template>
                            <template #percentage>
                                <span>{{ seasonData.surchargePercentageDisplay }}</span>
                            </template>
                        </i18n>
                    </div>
                </div>
            </template>
        </Accordion>

        <div class="vehicle-prices__subheading">{{ $t('pages.vehicle.additional_costs_and_conditions') }}</div>

        <ul class="overview-list">
            <OverviewListItem
                v-if="depositFieldValue !== null"
                :vehicle="vehicle"
                :field="depositFieldKey"
                :field-value="
                    $t('pricing.x_per_booking', {
                        price: formattedPrice(depositFieldValue, vehicle.currency, 0),
                    })
                "
                :tooltip-text="
                    $t('pages.vehicle.deposit_footnote', {
                        feePercentage: formattedPercentage(vehicle.extraChargesServiceFeePercentage),
                    })
                "
            />
            <OverviewListItem
                :vehicle="vehicle"
                field="cleaningFee"
                :field-value="
                    $t('pricing.x_per_booking', {
                        price: formattedPrice(vehicle.pricing.cleaningFee, vehicle.currency, 0),
                    })
                "
            />
            <li v-if="additionalDriverAddon && $te('pricing.additional_drivers')" class="overview-list-item">
                <label class="overview-list-item__label">{{ $t('pricing.additional_drivers') }}</label>
                <span class="overview-list-item__value">
                    {{
                        $t('pricing.x_per_night', {
                            price: formattedPrice(
                                additionalDriverAddon.price,
                                vehicle.currency,
                                ['eur', 'chf'].includes($store.getters.defaultCurrency) ? 1 : 0
                            ),
                        })
                    }}
                </span>
            </li>
            <template v-if="vehicle.includedDistance === -1">
                <OverviewListItem
                    :vehicle="vehicle"
                    field="includedDistance"
                    :field-value="$t('pages.vehicle.included_distance_unlimited_value')"
                />
            </template>
            <template v-else>
                <div v-if="vehicle.pricing && vehicle.pricing.numberOfNights">
                    <OverviewListItem
                        :vehicle="vehicle"
                        field="includedDistance"
                        :field-value="
                            $t('vehicle_fields.included_distance_per_booking', {
                                includedDistanceMiles: $options.filters.rounded(
                                    (vehicle.includedDistance * vehicle.pricing.numberOfNights) / 10
                                ),
                                includedDistanceKm: $options.filters.rounded(
                                    vehicle.includedDistance * vehicle.pricing.numberOfNights
                                ),
                            })
                        "
                    />
                </div>
                <template v-else>
                    <OverviewListItem
                        :vehicle="vehicle"
                        field="includedDistance"
                        :tooltip-text="
                            $t('pages.vehicle.included_distance_footnote', {
                                distanceMiles: exampleIncludedDistanceMiles,
                                distanceKm: exampleIncludedDistanceKm,
                            })
                        "
                    />
                </template>
                <OverviewListItem :vehicle="vehicle" field="pricePerKilometerIncFee" />
            </template>
        </ul>
    </div>
</template>

<script>
import { ADDITIONAL_DRIVER } from '~/config/productTypes';
import { SHORT_BOOKING_SURCHARGE_THRESHOLD, LONG_BOOKING_DISCOUNT_THRESHOLD } from '~/config/bookingProcess';
import OverviewListItem from '~/components/vehicle/OverviewListItem.vue';
import Accordion from '~/components/Accordion';

const HIGH_SEASON_KEY = 'high-season';
const LOW_SEASON_KEY = 'low-season';

export default {
    components: {
        OverviewListItem,
        Accordion,
    },
    props: {
        vehicle: {
            type: Object,
            default: () => {},
        },
        addons: {
            type: Array,
            default: null,
        },
    },
    data() {
        return {
            accordionItems: [
                {
                    title: this.$t('pages.edit_vehicle_pricing.high_season'),
                    key: HIGH_SEASON_KEY,
                    open: false,
                },
                {
                    title: this.$t('pages.edit_vehicle_pricing.low_season'),
                    key: LOW_SEASON_KEY,
                    open: false,
                },
            ],

            SHORT_BOOKING_SURCHARGE_THRESHOLD,
            LONG_BOOKING_DISCOUNT_THRESHOLD,
        };
    },
    computed: {
        lowNightlyPrice() {
            return this.formattedPrice(this.vehicle.pricing.lowNightlyPrice, this.vehicle.pricing.currency, 0);
        },
        highNightlyPrice() {
            return this.formattedPrice(this.vehicle.pricing.highNightlyPrice, this.vehicle.pricing.currency, 0);
        },
        depositFieldKey() {
            if (this.vehicle?.pricing?.cashDeposit) {
                return 'cashDeposit';
            }

            return 'deposit';
        },
        depositFieldValue() {
            if (this.vehicle?.pricing?.cashDeposit) {
                return this.vehicle.pricing.cashDeposit;
            }

            return this.vehicle.pricing.deposit;
        },
        exampleIncludedDistanceMiles() {
            return this.$root.$options.filters.rounded((this.vehicle.includedDistance * 4) / 10, 1);
        },
        exampleIncludedDistanceKm() {
            return this.$root.$options.filters.rounded(this.vehicle.includedDistance * 4);
        },
        additionalDriverAddon() {
            return this.addons?.find(p => p.type === ADDITIONAL_DRIVER);
        },
        processedSeasonData() {
            return [
                {
                    key: HIGH_SEASON_KEY,
                    nightlyPriceDisplay: this.highNightlyPrice,
                    minBookingLengthVal: this.vehicle.minBookingLengthHigh,
                    hasDiscount: this.vehicle.priceHighDiscount > 0,
                    discountPercentageDisplay: `${this.vehicle.priceHighDiscount * 100}%`,
                    hasIncrease: this.vehicle.priceHighIncrease > 0,
                    surchargePercentageDisplay: `${this.vehicle.priceHighIncrease * 100}%`,
                },
                {
                    key: LOW_SEASON_KEY,
                    nightlyPriceDisplay: this.lowNightlyPrice,
                    minBookingLengthVal: this.vehicle.minBookingLengthLow,
                    hasDiscount: this.vehicle.priceLowDiscount > 0,
                    discountPercentageDisplay: `${this.vehicle.priceLowDiscount * 100}%`,
                    hasIncrease: this.vehicle.priceLowIncrease > 0,
                    surchargePercentageDisplay: `${this.vehicle.priceHighIncrease * 100}%`,
                },
            ];
        },
    },
};
</script>

<style scoped lang="scss">
@import '@/sass/_variables.scss';

.vehicle-prices {
    &__subheading {
        @include font-size(18px);
        font-weight: 700;
        margin-bottom: 20px;

        @include lg {
            margin-bottom: 32px;
        }
    }

    &__seasons-accordion {
        margin-bottom: 32px;
    }

    &__accordion-item-header {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        gap: 4px;
        flex: 1;
        @include font-size(14px);

        strong {
            font-weight: 700;
            @include font-size(16px);
        }

        strong + span {
            margin-left: 8px;
        }

        @include lg {
            flex-direction: row;
            align-items: center;
            gap: 0;
        }
    }

    &__seasonal-info-top {
        @include font-size(16px);

        > div {
            display: flex;
            justify-content: space-between;
            padding-right: 26px;
            margin-bottom: 16px;

            &:last-child {
                margin-bottom: 0;
            }
        }
    }

    &__seasonal-info-bottom {
        border-top: 1px solid $border-color;
        margin-top: 16px;
        padding-top: 16px;
        @include font-size(16px);

        div {
            margin-bottom: 16px;

            &:last-child {
                margin-bottom: 0;
            }
        }
    }

    &__per-night-amount {
        @include font-size(16px);
    }
}
</style>
